import { relations } from 'drizzle-orm';
import {
  index,
  integer,
  real,
  sqliteTable,
  text
} from 'drizzle-orm/sqlite-core';

import { snapshotBaseColumns } from './baseColumns';
import { privatePosts } from './posts';

export const privatePostSnapshots = sqliteTable(
  'private_post_snapshots',
  {
    ...snapshotBaseColumns,
    postId: text('post_id')
      .notNull()
      .references(() => privatePosts.id, { onDelete: 'cascade' }),
    // Post state at snapshot time
    content: text('content').notNull(),
    spaceId: integer('space_id'),
    parentPostId: text('parent_post_id'),
    anonymous: integer('anonymous', { mode: 'boolean' }).notNull(),
    postCreatedAt: integer('post_created_at').notNull(),
    // Metadata
    source: text('source').notNull(), // 'client' | 'mobile'
    hashSent: integer('hash_sent') // Track sync status
  },
  (t) => [
    index('idx_snapshots_hash_sent').on(t.hashSent),
    index('snapshots_post_created').on(t.postId, t.createdAt)
  ]
);

export const snapshotUploads = sqliteTable(
  'snapshot_uploads',
  {
    ...snapshotBaseColumns,
    snapshotId: text('snapshot_id')
      .notNull()
      .references(() => privatePostSnapshots.id, { onDelete: 'cascade' }),
    uploadId: text('upload_id').notNull(), // Original upload ID
    key: text('key').notNull(), // Storage key
    type: text('type').notNull(), // IMAGE/VIDEO/DOCUMENT
    uploadCreatedAt: integer('upload_created_at').notNull()
  },
  (t) => [index('idx_snapshot_uploads_snapshot_id').on(t.snapshotId)]
);

export const snapshotFactiiiCounts = sqliteTable(
  'snapshot_factiii_counts',
  {
    ...snapshotBaseColumns,
    snapshotId: text('snapshot_id')
      .notNull()
      .references(() => privatePostSnapshots.id, { onDelete: 'cascade' }),
    countId: text('count_id').notNull(), // Original count ID
    factiiiId: integer('factiii_id').notNull(),
    count: integer('count').notNull(),
    countCreatedAt: integer('count_created_at').notNull()
  },
  (t) => [index('idx_sfc_snapshot_id').on(t.snapshotId)]
);

export const snapshotFactiiis = sqliteTable(
  'snapshot_factiiis',
  {
    ...snapshotBaseColumns,
    snapshotFactiiiCountId: text('snapshot_factiii_count_id')
      .notNull()
      .references(() => snapshotFactiiiCounts.id, { onDelete: 'cascade' }),
    factiiiId: text('factiii_id').notNull(), // Original factiii ID (stored as text)
    data: text('data'),
    number: real('number'), // INTEGER_REQUIRED's value
    anonymous: integer('anonymous', { mode: 'boolean' }).notNull(),
    factiiiCreatedAt: integer('factiii_created_at').notNull()
  },
  (t) => [index('idx_sf_count_id').on(t.snapshotFactiiiCountId)]
);

export const snapshotSpaceTimes = sqliteTable(
  'snapshot_space_times',
  {
    ...snapshotBaseColumns,
    snapshotFactiiiId: text('snapshot_factiii_id')
      .notNull()
      .references(() => snapshotFactiiis.id, { onDelete: 'cascade' }),
    spaceTimeId: text('space_time_id').notNull(), // Original ID
    timestamp: integer('timestamp'),
    latitude: real('latitude'),
    longitude: real('longitude'),
    display: text('display'),
    altitude: integer('altitude'),
    spaceTimeCreatedAt: integer('space_time_created_at').notNull()
  },
  (t) => [index('idx_sst_factiii_id').on(t.snapshotFactiiiId)]
);

export const snapshotFactiiiUploads = sqliteTable(
  'snapshot_factiii_uploads',
  {
    ...snapshotBaseColumns,
    snapshotFactiiiId: text('snapshot_factiii_id')
      .notNull()
      .references(() => snapshotFactiiis.id, { onDelete: 'cascade' }),
    uploadId: text('upload_id').notNull(), // Original upload ID
    key: text('key').notNull(), // Storage key
    type: text('type').notNull(), // IMAGE/VIDEO/DOCUMENT
    uploadCreatedAt: integer('upload_created_at').notNull()
  },
  (t) => [index('idx_sfu_factiii_id').on(t.snapshotFactiiiId)]
);

export const snapshotPostReferences = sqliteTable(
  'snapshot_post_references',
  {
    ...snapshotBaseColumns,
    snapshotFactiiiId: text('snapshot_factiii_id')
      .notNull()
      .references(() => snapshotFactiiis.id, { onDelete: 'cascade' }),
    postUuid: text('post_uuid').notNull()
  },
  (t) => [index('idx_spr_factiii_id').on(t.snapshotFactiiiId)]
);

export const snapshotFactiiiReferences = sqliteTable(
  'snapshot_factiii_references',
  {
    ...snapshotBaseColumns,
    snapshotFactiiiId: text('snapshot_factiii_id')
      .notNull()
      .references(() => snapshotFactiiis.id, { onDelete: 'cascade' }),
    factiiiId: integer('factiii_id').notNull(),
    data: text('data')
  },
  (t) => [index('idx_sfr_factiii_id').on(t.snapshotFactiiiId)]
);

export const privatePostSnapshotsRelations = relations(
  privatePostSnapshots,
  ({ one, many }) => ({
    post: one(privatePosts, {
      fields: [privatePostSnapshots.postId],
      references: [privatePosts.id]
    }),
    uploads: many(snapshotUploads),
    factiiiCounts: many(snapshotFactiiiCounts)
  })
);

export const snapshotUploadsRelations = relations(
  snapshotUploads,
  ({ one }) => ({
    snapshot: one(privatePostSnapshots, {
      fields: [snapshotUploads.snapshotId],
      references: [privatePostSnapshots.id]
    })
  })
);

export const snapshotFactiiiCountsRelations = relations(
  snapshotFactiiiCounts,
  ({ one, many }) => ({
    snapshot: one(privatePostSnapshots, {
      fields: [snapshotFactiiiCounts.snapshotId],
      references: [privatePostSnapshots.id]
    }),
    factiiis: many(snapshotFactiiis)
  })
);

export const snapshotFactiiisRelations = relations(
  snapshotFactiiis,
  ({ one, many }) => ({
    factiiiCount: one(snapshotFactiiiCounts, {
      fields: [snapshotFactiiis.snapshotFactiiiCountId],
      references: [snapshotFactiiiCounts.id]
    }),
    spaceTimes: many(snapshotSpaceTimes),
    uploads: many(snapshotFactiiiUploads),
    postReferences: many(snapshotPostReferences),
    factiiiReferences: many(snapshotFactiiiReferences)
  })
);

export const snapshotPostReferencesRelations = relations(
  snapshotPostReferences,
  ({ one }) => ({
    factiii: one(snapshotFactiiis, {
      fields: [snapshotPostReferences.snapshotFactiiiId],
      references: [snapshotFactiiis.id]
    })
  })
);

export const snapshotFactiiiReferencesRelations = relations(
  snapshotFactiiiReferences,
  ({ one }) => ({
    factiii: one(snapshotFactiiis, {
      fields: [snapshotFactiiiReferences.snapshotFactiiiId],
      references: [snapshotFactiiis.id]
    })
  })
);

export const snapshotSpaceTimesRelations = relations(
  snapshotSpaceTimes,
  ({ one }) => ({
    factiii: one(snapshotFactiiis, {
      fields: [snapshotSpaceTimes.snapshotFactiiiId],
      references: [snapshotFactiiis.id]
    })
  })
);

export const snapshotFactiiiUploadsRelations = relations(
  snapshotFactiiiUploads,
  ({ one }) => ({
    factiii: one(snapshotFactiiis, {
      fields: [snapshotFactiiiUploads.snapshotFactiiiId],
      references: [snapshotFactiiis.id]
    })
  })
);
